import type { Node as PmNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";
import type { Msg } from "../types";
import { ViewBlockNodeView } from "./view-node-view";
import { CardLinkNodeView } from "./card-link-node-view";
import { CardSectionView } from "./card-section";
import type { OpenPreview, SectionEditor } from "./card-section";

type Dispatch = (msg: Msg) => void;
type NodeViewCtor = (node: PmNode, view: EditorView, getPos: () => number | undefined) => {
  dom: HTMLElement;
};

// A read-only preview shows the cards that it links to as more previews, so
// previews nest. At this depth, links, sections, and view blocks show an
// ellipsis instead.
const MAX_PREVIEW_DEPTH = 2;

// The node views of an editor of a card. `path` holds the cards from the main
// card to the card of this editor. `openEditor` makes the editor of a section,
// and `openPreview` shows a card that is open above. The caller gives them,
// because an editor contains sections and a section contains an editor or a
// preview.
export function liveNodeViews(
  path: string[],
  dispatch: Dispatch,
  editable: boolean,
  openEditor: (cardId: string, el: HTMLElement, path: string[], editable: boolean) => SectionEditor,
  openPreview: OpenPreview
): Record<string, NodeViewCtor> {
  const cardId = path[path.length - 1]!;
  // A read-only editor is the body of a collapsed section, which shows only the
  // title block. A section or a view block is never that block, so these get an
  // empty node view, which loads no cards and runs no query.
  if (!editable) {
    return {
      view_block: () => new EmptyNodeView(),
      card_link: (node, view, getPos) => new CardLinkNodeView(node, view, getPos, dispatch, true),
      card_embed: () => new EmptyNodeView(),
    };
  }
  return {
    view_block: (node, view, getPos) =>
      new ViewBlockNodeView(node, view, getPos, dispatch, { kind: "live", cardId }),
    card_link: (node, view, getPos) => new CardLinkNodeView(node, view, getPos, dispatch, true),
    card_embed: (node, view, getPos) =>
      new CardSectionView(node, view, getPos, dispatch, {
        kind: "live",
        path,
        openEditor: (id, el, sectionEditable) => openEditor(id, el, [...path, id], sectionEditable),
        openPreview,
      }),
  };
}

// The node views of a read-only preview at `depth`. The live editor is depth 0.
// `openPreview` shows the card of a section one level deeper.
export function previewNodeViews(
  depth: number,
  dispatch: Dispatch,
  openPreview: OpenPreview
): Record<string, NodeViewCtor> {
  if (depth >= MAX_PREVIEW_DEPTH) {
    return {
      card_link: () => new EllipsisNodeView("inline"),
      card_embed: () => new EllipsisNodeView("block"),
      view_block: () => new EllipsisNodeView("block"),
    };
  }
  return {
    view_block: (node, view, getPos) =>
      new ViewBlockNodeView(node, view, getPos, dispatch, { kind: "preview", depth }),
    card_link: (node, view, getPos) => new CardLinkNodeView(node, view, getPos, dispatch, false),
    card_embed: (node, view, getPos) =>
      new CardSectionView(node, view, getPos, dispatch, { kind: "preview", openPreview }),
  };
}

class EmptyNodeView {
  dom = document.createElement("div");
  ignoreMutation(): boolean {
    return true;
  }
}

// This node view replaces a link, a section, or a view block at the depth limit.
class EllipsisNodeView {
  dom: HTMLElement;
  constructor(kind: "inline" | "block") {
    this.dom = document.createElement(kind === "inline" ? "span" : "div");
    this.dom.className = kind === "inline" ? "card-link-ellipsis" : "view-block-ellipsis";
    this.dom.textContent = "…";
    this.dom.contentEditable = "false";
  }
  ignoreMutation(): boolean {
    return true;
  }
  stopEvent(): boolean {
    return false;
  }
}
